import { useState } from "react";
import type { BuildPlanResponse } from "../wizardTypes";
import { PlanLineCard } from "../components/PlanLineCard";
import { registryGateFor, NEED_LABELS, fmtMoney } from "../data/planMatch";
import { CARD_LIMIT } from "../data/rackRates";
import type { Answers, WizardStep } from "../wizardTypes";
import { bookMeetingUrl } from "../wizardTypes";
import styles from "../css/SignupWizard.module.css";

type Props = {
  plan: BuildPlanResponse;
  answers: Answers;
  onConfirm: () => void;
  onGoToStep?: (step: WizardStep) => void;
  onToSales?: (reason: string, email?: string, navigate?: boolean) => void;
};


export function PlanStep({ plan, answers, onConfirm, onGoToStep, onToSales }: Props) {
  const [agreed, setAgreed] = useState(false);

  const hasScrub = plan.lines.some((l) => l.productKey === "dncscrub");
  const registryGate = hasScrub ? registryGateFor("scrub") : null;


  const priceLabel = plan.monthlyTotal > 0
    ? fmtMoney(plan.monthlyTotal)
    : fmtMoney(plan.annualTotal);
  const pricePer = plan.monthlyTotal > 0 ? "/mo" : "/yr";

  // Safety net: if overCardLimit somehow reaches this step, show sales CTA
  if (plan.overCardLimit) {
    return (
      <div className={styles.stepSingle}>
        <h2 className={styles.pageTitle}>
          Let&rsquo;s find the right plan for you
        </h2>
        <p>
          Your plan exceeds ${CARD_LIMIT.toLocaleString()}/mo. Our team will
          set up your account directly.
        </p>
        <a
          href={bookMeetingUrl(answers.email)}
          target="_top"
          rel="noopener noreferrer"
          className={styles.btnPrimary}
          style={{ textDecoration: "none" }}
        >
          Book a Meeting
        </a>
      </div>
    );
  }

  return (
    <div className={styles.planWrap}>
      <p className={styles.planProgress}>
        <span className={styles.planProgressStep}>Step 2 of 3</span> &middot;
        Your plan
      </p>
      <h2 className={styles.pageTitle} style={{ margin: "0.35rem 0 0" }}>
        Here&rsquo;s your compliance plan.
      </h2>

      {/* Recommended chips */}
      <p className={styles.planBecause}>
        Recommended because you told us:
        <span className={styles.planChips}>
          {answers.needs.map((n) => (
            <span key={n} className={styles.planChip}>
              {NEED_LABELS[n] ?? n}
            </span>
          ))}
        </span>
      </p>

      <div className={styles.stepGrid}>
        {/* Left column: plan lines + content */}
        <div>
          <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
            {plan.lines.map((line) => (
              <li key={line.productKey}>
                <PlanLineCard line={line}>
                  {/* Registry gate inside scrub card */}
                  {line.productKey === "dncscrub" && registryGate && (
                    <div className={styles.registryGate}>
                      <p className={styles.registryGateLabel}>
                        {registryGate.title}
                      </p>
                      <ul>
                        {registryGate.rows.map((row, i) => (
                          <li key={i}>
                            <strong>{row.bold}</strong>
                            {row.text}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </PlanLineCard>
              </li>
            ))}
          </ul>

          {/* Adjust plan */}
          {onGoToStep && (
            <p className={styles.planAdjust}>
              <button
                type="button"
                onClick={() => onGoToStep("questionnaire")}
              >
                Adjust plan
              </button>{" "}
              <span className={styles.planAdjustHint}>
                Change volumes or add products
              </span>
            </p>
          )}

          {/* What happens next */}
          <h3 className={styles.planNextTitle}>What happens next</h3>
          <ol className={styles.planNext}>
            <li>
              <strong>Add your payment details</strong>
              <span>
                Charged today. Monthly term, cancel with 30 days&rsquo; notice.
              </span>
            </li>
            <li>
              <strong>Upload a list</strong>
              <span>CSV or API &mdash; whichever you already use.</span>
            </li>
            <li>
              <strong>See your hits</strong>
              <span>Every flagged number, with the reason.</span>
            </li>
          </ol>

          {/* FAQ */}
          <div className={styles.planFaq}>
            <details open>
              <summary>Do I need my own registry subscriptions?</summary>
              <p>
                <strong>
                  Only if you&rsquo;re calling people who haven&rsquo;t opted
                  in.
                </strong>{" "}
                The federal Do Not Call registry governs cold outreach to
                consumers. If every number you dial has given you express
                written consent, or you&rsquo;re inside an established business
                relationship &mdash; 18 months from their last purchase,
                payment or delivery, 3 months from an enquiry or application
                &mdash; those calls are exempt, and so are calls to business
                numbers. No SAN needed for that traffic.
              </p>
              <p>
                If you do make non-opt-in consumer calls, the subscriptions are
                yours to hold: a <strong>SAN</strong> in the US, priced per
                area code and renewed annually, and in Canada a{" "}
                <strong>RAN</strong> to register plus a paid subscription
                before anyone may download or query the National DNCL.
              </p>
              <p>
                <strong>Two things people get caught by.</strong> The
                business-relationship exemption covers live-agent calls only
                &mdash; it does not cover autodialed or pre-recorded calls. And
                an opt-in list is not the end of it: consent attaches to a
                person, not a phone number, so when a number is reassigned your
                consent is void and there&rsquo;s no safe harbor if you
                didn&rsquo;t check. That&rsquo;s why an all-consent caller
                still needs reassigned-number checks, litigator screening and
                internal DNC &mdash; even with no registry subscription at all.
              </p>
            </details>
            <details>
              <summary>Am I signing a contract?</summary>
              <p>
                Yes &mdash; the{" "}
                <a
                  href="https://www.dnc.com/services-agreement"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Services Agreement
                </a>
                , which you accept below. The term is monthly and renews
                automatically; either side can end it with 30 days&rsquo;
                notice before the current term expires. Annual terms are
                available if you want the discount.
              </p>
            </details>
            <details>
              <summary>What if it turns out not to be right for us?</summary>
              <p>
                The term is monthly, so you&rsquo;re never locked into a year.
                Give 30 days&rsquo; notice before the current term ends and it
                stops there.
              </p>
            </details>
            <details>
              <summary>What happens to the numbers I upload?</summary>
              <p>
                Your lists are yours. We scrub and return them, we don&rsquo;t
                resell them, and trial uploads are deleted on request.
              </p>
            </details>
          </div>
        </div>

        {/* Right column: sidebar */}
        <aside className={styles.planSide}>
          {/* Offer card — card up front (Phase 1) */}
          <div className={styles.offerCard}>
            <p className={styles.offerLabel}>Your plan</p>
            <p className={styles.offerAmount}>
              {priceLabel}
              <span className={styles.offerPer}>{pricePer}</span>
            </p>
            <p className={styles.offerGuarantee}>
              <strong>Monthly term.</strong> Renews automatically. Requires 30
              days cancellation notice.
            </p>
            <button
              type="button"
              className={styles.trialCta}
              disabled={!agreed}
              onClick={onConfirm}
            >
              Continue to payment
            </button>
            <p className={styles.trialFoot}>
              Bills Monthly &middot; Renews Automatically
            </p>
            {onGoToStep && (
              <p className={styles.payBack}>
                <button type="button" onClick={() => onGoToStep("questionnaire")}>
                  &larr; Change your answers
                </button>
              </p>
            )}
          </div>

          {/* Consent */}
          <label className={styles.planConsent}>
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
            />
            <span>
              I agree to the{" "}
              <a
                href="https://www.dnc.com/services-agreement"
                target="_blank"
                rel="noopener noreferrer"
              >
                Services Agreement
              </a>{" "}
              and{" "}
              <a
                href="https://www.dnc.com/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy
              </a>
              .
            </span>
          </label>

          {/* Fine print */}
          <div className={styles.planFine}>
            <p>
              Prefer a walkthrough?{" "}
              <a
                href={bookMeetingUrl(answers.email)}
                target="_top"
                rel="noopener noreferrer"
                onClick={() => onToSales?.("plan_link", undefined, false)}
              >
                Talk to our compliance team
              </a>
            </p>
          </div>

          {/* Trust line */}
          <p className={styles.planTrust}>
            <strong>20+ Years</strong> &middot;{" "}
            <strong>140 Billion</strong> Numbers Scrubbed &middot;{" "}
            <strong>Zero</strong> Client Fines
          </p>
        </aside>
      </div>

      {/* Enterprise footer */}
      <p className={styles.planEnterprise}>
        Over 1M numbers a month, or need an MSA, SSO or a security review?{" "}
        <a
          href={bookMeetingUrl(answers.email)}
          target="_top"
          rel="noopener noreferrer"
          onClick={() => onToSales?.("plan_link", undefined, false)}
        >
          Talk to sales
        </a>{" "}
        &mdash; we&rsquo;ll also scope a full-volume paid pilot.
      </p>
    </div>
  );
}
